import {
	TableCell,
	TableRow,
	Badge,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	Button
} from '@/components/ui';
import { PromotionStatusEnum } from '@/enums';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MoreHorizontal, Trash2, Clock, Play, CheckCircle, Eye } from 'lucide-react';
import type { IPromotion } from '@/interfaces';
import { PROMOTION_PRODUCT_LABELS, PROMOTION_STATUS_CONFIG } from '@/constants';

const STATUS_ACTIONS: { status: PromotionStatusEnum; label: string; icon: React.ReactNode }[] = [
	{ status: PromotionStatusEnum.PENDING, label: 'En attente', icon: <Clock className="mr-2 h-4 w-4" /> },
	{ status: PromotionStatusEnum.ACTIVE, label: 'En cours', icon: <Play className="mr-2 h-4 w-4" /> },
	{ status: PromotionStatusEnum.FINISHED, label: 'Terminer', icon: <CheckCircle className="mr-2 h-4 w-4" /> }
];

type TProps = {
	promotion: IPromotion;
	onSetStatus: (promotion: IPromotion, status: PromotionStatusEnum) => void;
	onDelete: (promotion: IPromotion) => void;
	onViewDetails: (promotion: IPromotion) => void;
	canDelete: boolean;
};

const formatDate = (d: string) => {
	try {
		return format(new Date(d), 'dd MMM yyyy', { locale: fr });
	} catch {
		return d;
	}
};

const DataTableDesktopRow = ({ promotion, onSetStatus, onDelete, onViewDetails, canDelete }: TProps) => {
	const statusCfg = PROMOTION_STATUS_CONFIG[promotion.status] ?? PROMOTION_STATUS_CONFIG[PromotionStatusEnum.PENDING];

	return (
		<TableRow className="h-14 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => onViewDetails(promotion)}>
			<TableCell className="py-2">
				<div className="flex flex-wrap gap-1">
					{promotion.products.map(p => (
						<Badge key={p} variant="secondary" className="text-xs">
							{PROMOTION_PRODUCT_LABELS[p] ?? p}
						</Badge>
					))}
				</div>
			</TableCell>
			<TableCell className="py-2 font-medium">{promotion.percentage}%</TableCell>
			<TableCell className="py-2 text-sm text-muted-foreground">{formatDate(promotion.from)}</TableCell>
			<TableCell className="py-2 text-sm text-muted-foreground">{formatDate(promotion.to)}</TableCell>
			<TableCell className="py-2">
				<Badge className={cn('text-xs font-medium', statusCfg.className)}>{statusCfg.label}</Badge>
			</TableCell>
			<TableCell className="py-2 text-right">
				<div className="flex items-center justify-end gap-1">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="icon" className="h-8 w-8">
								<span className="sr-only">Actions</span>
								<MoreHorizontal size={14} />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem
								className="cursor-pointer"
								onClick={e => {
									e.stopPropagation();
									onViewDetails(promotion);
								}}
							>
								<Eye className="mr-2 h-4 w-4" />
								Voir
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							{promotion.status !== PromotionStatusEnum.FINISHED && STATUS_ACTIONS.filter(a => a.status !== promotion.status).map(a => (
								<DropdownMenuItem
									key={a.status}
									className="cursor-pointer"
									onClick={e => {
										e.stopPropagation();
										onSetStatus(promotion, a.status);
									}}
								>
									{a.icon}
									{a.label}
								</DropdownMenuItem>
							))}
							{canDelete && (
								<>
									<DropdownMenuSeparator />
									<DropdownMenuItem
										className="text-destructive cursor-pointer"
										onClick={e => {
											e.stopPropagation();
											onDelete(promotion);
										}}
									>
										<Trash2 className="mr-2 h-4 w-4" />
										Supprimer
									</DropdownMenuItem>
								</>
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</TableCell>
		</TableRow>
	);
};

export default DataTableDesktopRow;
